/**
 * Что просим у игрока в Mini App ради возврата (ADR-0010, план окупаемости): право писать боту и ярлык на главный экран.
 * Автовход чат с ботом не открывает: в Sapero бот мог писать 23 из 1 943 аккаунтов.
 *
 * Право: после обычной волны не ниже {@link WRITE_ACCESS_WAVE} (ISSUE-143: первая награда — задания и серия открываются там же;
 * на боссе 10 до просьбы доходили единицы) окно с объяснением встаёт в очередь перед выбором усиления, на боссах не спрашиваем
 * (там окно победы и «Позови друга»). «Разрешить» открывает системное окно Telegram. Отказ («Не сейчас»
 * или в системном окне) не спрашиваем повторно: `allowsWriteToPm = false` на сервере и флаг на устройстве, на случай если
 * запрос не дошёл. Старый клиент без ответа отказом не считается.
 *
 * Ярлык: со второй сессии (ISSUE-136: в первые минуты игроку ещё нечего добавлять на экран), на обычной волне (окно
 * победы не закрываем) и не раньше {@link SYSTEM_WINDOW_GAP_MS} после ответа о праве: два запроса подряд раздражают.
 * Сначала наше окно: на iOS `addToHomeScreen` не системное окно, а уход из игры в Safari, поэтому он только по
 * «Добавить». Добавил или телефон не умеет — больше не предлагаем, «Не сейчас» или закрыл страницу — через
 * {@link HOME_SCREEN_RETRY_MS}.
 */
import type { PlayerState } from "@newfungame/contracts";
import { analytics } from "../analytics";
import type { ApiTransport } from "../api";
import { setWindowActions } from "../ui/windows/actions";
import { appStore, openWindow, setPlayer } from "./store";
import { homeScreenStatus, requestWriteAccess, type HomeScreenStatus, type TelegramWebApp } from "./telegram";

export const WRITE_ANSWERED_KEY = "nfg:writeAccess";
export const HOME_SCREEN_KEY = "nfg:homeScreen";
export const HOME_SCREEN_RETRY_MS = 7 * 24 * 3_600_000;
export const SYSTEM_WINDOW_GAP_MS = 60_000;
/** С этой волны просим право писать: вместе с первой наградой (задания и серия, `SIDE_UNLOCK`). */
export const WRITE_ACCESS_WAVE = 3;
/** Когда аккаунт впервые открыт на этом устройстве: вторая сессия — заход позже чем через {@link SESSION_GAP_MS}. */
export const FIRST_SEEN_KEY = "nfg:firstSeen";
export const SESSION_GAP_MS = 30 * 60 * 1000;

type Store = Pick<Storage, "getItem" | "setItem">;
interface HomeRecord {
  at: number;
  result: "asked" | "done";
}

function readHome(storage: Store | null): HomeRecord | null {
  try {
    const r = JSON.parse(storage?.getItem(HOME_SCREEN_KEY) ?? "null") as Partial<HomeRecord> | null;
    return r && typeof r.at === "number" && (r.result === "asked" || r.result === "done")
      ? { at: r.at, result: r.result }
      : null;
  } catch {
    return null; // битая запись — как будто не предлагали
  }
}

function writeHome(storage: Store | null, result: HomeRecord["result"], at: number): void {
  try {
    storage?.setItem(HOME_SCREEN_KEY, JSON.stringify({ at, result }));
  } catch {
    // без localStorage окно останется разовым на сессию
  }
}

/** Пора ли спрашивать Telegram о ярлыке: не предлагали или предлагали давно. */
export function homeScreenDue(storage: Store | null, now: number): boolean {
  const r = readHome(storage);
  return !r || (r.result === "asked" && now - r.at >= HOME_SCREEN_RETRY_MS);
}

/** Показать ли окно при таком статусе; `added` и `unsupported` запоминаются навсегда. */
export function takeHomeScreenAsk(storage: Store | null, status: HomeScreenStatus | null, now: number): boolean {
  if (status === null) return false;
  if (status === "added" || status === "unsupported") {
    writeHome(storage, "done", now);
    return false;
  }
  writeHome(storage, "asked", now);
  return true;
}

export interface AsksDeps {
  /** WebApp внутри Mini App, иначе `null`. */
  tg(): TelegramWebApp | null;
  player(): PlayerState | null;
  setAllowed(allowed: boolean): void;
  openWriteAccess(): void;
  openHomeScreen(): void;
  save(allowed: boolean): Promise<unknown>;
  storage: Store | null;
  now(): number;
  /** Идёт не первая сессия игрока. */
  secondSession(): boolean;
  writeAccessWave: number;
  /** Трекер в момент события: аналитика стартует позже. */
  track(): Pick<ReturnType<typeof analytics>, "writeAccessAsk" | "writeAccess" | "homeScreenAsk" | "homeScreenAdded">;
}

export function createAsks(deps: AsksDeps) {
  const { storage } = deps;
  let answeredAt = -Infinity;
  /** Ярлык на этой сессии уже предлагали: подписка на `homeScreenAdded` — одна. */
  let homeAsked = false;
  /** Волна, на которой открыто наше окно о ярлыке. */
  let homeWave = 0;

  const answeredHere = (): boolean => {
    try {
      return storage?.getItem(WRITE_ANSWERED_KEY) === "1";
    } catch {
      return false;
    }
  };

  const settle = (allowed: boolean, via: "prompt" | "telegram"): void => {
    answeredAt = deps.now();
    try {
      storage?.setItem(WRITE_ANSWERED_KEY, "1");
    } catch {
      // без localStorage остаётся флаг на сервере
    }
    deps.track().writeAccess(allowed, via);
    deps.setAllowed(allowed);
    deps.save(allowed).catch(() => {
      // нет сети: на этом устройстве больше не спросим, напоминаний не будет
    });
  };

  const askHomeScreen = async (tg: TelegramWebApp, wave: number): Promise<void> => {
    const now = deps.now();
    if (homeAsked || !tg.addToHomeScreen || now - answeredAt < SYSTEM_WINDOW_GAP_MS) return;
    if (!homeScreenDue(storage, now)) return;
    homeAsked = true;
    if (!takeHomeScreenAsk(storage, await homeScreenStatus(tg), deps.now())) return;
    homeWave = wave;
    deps.track().homeScreenAsk(wave);
    deps.openHomeScreen();
  };

  return {
    /** Волна пройдена, бой стоит до выбора усиления. */
    waveCleared(wave: number, boss: boolean): void {
      const tg = deps.tg();
      const p = deps.player();
      if (!tg || !p || p.player.telegramId === null) return;
      const undecided = p.player.allowsWriteToPm === null && !answeredHere();
      // окно победы над боссом не нагружаем
      if (boss) return;
      if (undecided && wave >= deps.writeAccessWave && tg.requestWriteAccess) {
        deps.track().writeAccessAsk(wave);
        deps.openWriteAccess();
        return;
      }
      if (deps.secondSession()) void askHomeScreen(tg, wave);
    },

    /** Ответ в нашем окне. */
    async answer(allow: boolean): Promise<void> {
      if (!allow) return settle(false, "prompt");
      const tg = deps.tg();
      const allowed = tg ? await requestWriteAccess(tg) : null;
      if (allowed !== null) settle(allowed, "telegram");
    },

    /** Ответ в окне о ярлыке; «Не сейчас» спросим через неделю. */
    answerHomeScreen(add: boolean): void {
      const tg = deps.tg();
      if (!add || !tg?.addToHomeScreen) return;
      const wave = homeWave;
      tg.onEvent?.("homeScreenAdded", () => {
        writeHome(storage, "done", deps.now());
        deps.track().homeScreenAdded(wave);
      });
      try {
        tg.addToHomeScreen();
      } catch {
        // WebAppMethodUnsupported — следующая попытка через неделю
      }
    },
  };
}

function localStore(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Вторая сессия: аккаунт создан не этим входом, а эта загрузка игры — позже чем через {@link SESSION_GAP_MS} после первой
 * на устройстве (быстрый перезаход после вылета — та же сессия, долгая игра без перезахода — тоже). `loadAt` — время
 * загрузки страницы.
 */
export function isSecondSession(storage: Store | null, created: boolean, loadAt: number): boolean {
  let first: number | null = null;
  try {
    const raw = Number(storage?.getItem(FIRST_SEEN_KEY));
    first = Number.isFinite(raw) && raw > 0 ? raw : null;
  } catch {
    // без хранилища решает только флаг сервера
  }
  if (first === null) {
    try {
      // аккаунт старше этого устройства: первая сессия была давно, отмечаем её началом эпохи
      storage?.setItem(FIRST_SEEN_KEY, String(created ? loadAt : 1));
    } catch {
      // не запомнили — в следующий заход решит флаг сервера
    }
    return !created;
  }
  return loadAt - first >= SESSION_GAP_MS;
}

/** Подключает окно «может ли бот писать»; `waveCleared` зовёт бой. */
export function setupAsks(api: ApiTransport): ReturnType<typeof createAsks> {
  const loadAt = Date.now();
  const asks = createAsks({
    tg: () => (appStore.get().miniApp ? (window.Telegram?.WebApp ?? null) : null),
    player: () => appStore.get().player,
    setAllowed: (allowsWriteToPm) => {
      const p = appStore.get().player;
      if (p) setPlayer({ ...p, player: { ...p.player, allowsWriteToPm } });
    },
    openWriteAccess: () => openWindow({ kind: "writeAccess" }),
    openHomeScreen: () => openWindow({ kind: "homeScreen" }),
    save: (allowed) => api.call("setWriteAccess", { allowed }),
    storage: localStore(),
    now: () => Date.now(),
    secondSession: () => isSecondSession(localStore(), appStore.get().auth.created, loadAt),
    writeAccessWave: WRITE_ACCESS_WAVE,
    track: analytics,
  });
  // отладка окон (`?mock=1`) пишет вызовы в журнал сама
  if (!window.__nfgWindows) {
    setWindowActions({
      answerWriteAccess: (allow) => void asks.answer(allow),
      answerHomeScreen: (add) => asks.answerHomeScreen(add),
    });
  }
  return asks;
}
